import { REPORT_LOOK } from '../meshRenderer.js'

/**
 * renderScenes.js — a drafted document's scenes → rendered view images.
 *
 * Two passes, and the second one is the whole point:
 *
 *   1. Ask each scene what camera scale it would frame ITSELF at.
 *   2. Render every scene at the MAXIMUM scale within its fit group.
 *
 * Scenes sharing a `fitGroup` are the ones a reader is invited to compare —
 * Native beside Post Plan. If each were framed to itself the two columns would
 * be drawn at different magnifications and the page would silently misrepresent
 * the plan: a mandible that moved 5 mm can be made to look unchanged, or a
 * change invented where there is none. Taking the max means both are framed to
 * whichever is larger, so they are measurable against each other.
 *
 * A scene in a group of one is unaffected — max of one value is that value —
 * so splints and guides still fill their own frame.
 *
 * `renderReport` and the byte loader are INJECTED so this can be unit-tested
 * without a browser, a GPU or a case folder.
 */

import { GRID_VIEWS } from './schema.js'

/** The fallback view set for a scene no page references — the same single-sourced six. */
export const SCENE_VIEWS = GRID_VIEWS

/**
 * The views `renderReport` draws without being asked: the six orthographic
 * codes plus the isometric.
 *
 * Anything a document asks for BEYOND these is an extra-camera request and has
 * to be declared to the renderer (`extraViews`), because those cameras are
 * deliberately opt-in — the packaging label and the STL/OBJ tool render every
 * view the renderer knows by default and must not gain one.
 *
 * Derived here rather than imported from meshRenderer's own table on purpose:
 * `renderReport` is INJECTED into this module so it can be tested without a
 * browser, and reaching into the real renderer for a constant would undo that.
 */
const STANDARD_VIEWS = new Set([...GRID_VIEWS, 'ISO'])

/**
 * The house render settings for a case report. Matched to the STL/OBJ tool's
 * matcap look so a case report and a preview PDF of the same mesh agree.
 */
export const REPORT_RENDER_OPTS = {
  // THE shared look (meshRenderer.js) — matcap, bone tone, GTAO, key light.
  // This used to be a hand-written option set that had drifted badly: it never
  // passed `matcapUrl` at all, so the tuned look was simply absent, and with no
  // `boneColor` the default tone was read as an explicit tint and darkened the
  // model ~18%. Bone came out flat olive-taupe where the delivered report is
  // ivory. Inheriting the definition is what stops that recurring.
  ...REPORT_LOOK,

  // ── case-report specifics, applied ON TOP ─────────────────────────────────
  res: 1400,
  // The page draws its own view badges and its own tiled watermark, so the
  // renderer must not burn either into the image.
  cornerLabels: false,
  watermark: false,
  // POLYPAINT ON — deliberately different from the STL/OBJ tool, where it is
  // opt-in and defaults OFF because that tool has a Colours panel the team
  // drives by hand. A case report is generated FROM the team's ZBrush exports,
  // and those files carry the colours they chose as #MRGB. A file with no
  // polypaint still falls back to its role colour.
  usePolypaint: true,
  // Splints, guides and appliances render against the neutral lit-sphere so they
  // read as white resin rather than cream bone. Only the case report asks for
  // this: it is the only surface whose parts carry a role, and therefore the only
  // one that can tell a splint from a skull. See NON_ANATOMY_ROLES in palette.js.
  neutralNonAnatomy: true,
}


/**
 * Which views does the DOCUMENT actually ask of each scene?
 *
 * renderScenes used to shoot a fixed six — A/P/LL/RL/S/I — for every scene,
 * which was right while `grid` was the only block that used one. The moment the
 * Source data page and the print-models gallery asked for an ISO, every one of
 * those images came back blank: the writer warned "missing image for scene s001
 * view ISO" rather than drawing an empty box silently, which is how this was
 * caught, but nothing had rendered it.
 *
 * Deriving the set from the pages fixes that for every block, present and
 * future, and cuts work as a side effect: a gallery scene needs ONE view, not
 * six.
 */
export function viewsForScenes(doc) {
  const map = new Map()
  const add = (sceneId, view) => {
    if (!sceneId || !view) return
    if (!map.has(sceneId)) map.set(sceneId, new Set())
    map.get(sceneId).add(view)
  }
  // Array.isArray on every field, because `rows` means two different things in
  // this schema: a grid page's ROW COUNT (a number) and a measure page's list of
  // panels. Walking a grid's `rows: 3` threw "number 3 is not iterable" the
  // first time this ran. Worth remembering if the schema grows.
  const each = (v) => (Array.isArray(v) ? v : [])
  for (const p of doc.pages ?? []) {
    for (const c of each(p.cells)) add(c.sceneId, c.view)
    for (const im of each(p.images)) if (im?.kind === 'scene') add(im.sceneId, im.view)
    for (const it of each(p.items)) add(it.sceneId, it.view)
    for (const st of each(p.steps)) add(st.sceneId, st.view)
    for (const r of each(p.rows)) add(r.sceneId, r.view)
  }
  return map
}

/**
 * @param {object} doc                 a drafted ReportDoc (needs .scenes)
 * @param {object} sceneParts          sceneId → [{name, color, handle}] from draftReport
 * @param {object} opts
 * @param {(part:object) => Promise<Uint8Array>} opts.loadBytes
 * @param {Function} opts.renderReport meshRenderer.renderReport
 * @param {string[]} [opts.views]
 * @param {object}   [opts.renderOpts] merged over REPORT_RENDER_OPTS
 * @param {(done:number,total:number,label:string)=>void} [opts.onProgress]
 * @param {number}   [opts.byteBudget] cap on mesh bytes held between the two
 *        passes (default 256 MB). Beyond it, meshes are re-read rather than
 *        kept — Case 4 alone is 458 MB of meshes.
 * @returns {Promise<{views: object, fitScales: object, groupScale: object}>}
 *          `views` is sceneId → { A: dataUrl, … }
 */
/**
 * The frame options ONE scene pins, or nothing.
 *
 * A scene's `frame` is now null unless something DECLARED it (schema.js newScene — the previous
 * stamped default made every scene read as a declaration and killed the operator's Flip
 * front/back toggle; the root cause is fixed there, so this honours any declared frame rather
 * than inferring intent from one flag). Undeclared scenes follow the operator's global look.
 * One legacy shape is still recognised as UNdeclared: a bare `{ flipAP: true }` with no other
 * keys is exactly the old stamped default, carried by every doc saved before the fix — honouring
 * it would re-kill the toggle for reloaded documents.
 */
export const sceneFrameOpts = (frame) => {
  if (!frame) return {}
  const keys = Object.keys(frame)
  if (keys.length === 1 && frame.flipAP === true) return {}   // the old stamped default
  return {
    ...(typeof frame.flipAP === 'boolean' ? { flipAP: frame.flipAP } : {}),
    ...(typeof frame.lpsFrame === 'boolean' ? { lpsFrame: frame.lpsFrame } : {}),
  }
}

export async function renderScenes(doc, sceneParts, {
  loadBytes, renderReport, views = SCENE_VIEWS, renderOpts = {}, onProgress = null,
  byteBudget = 256 * 1024 * 1024,
} = {}) {
  if (typeof loadBytes !== 'function') throw new TypeError('renderScenes: loadBytes is required')
  if (typeof renderReport !== 'function') throw new TypeError('renderScenes: renderReport is required')

  const scenes = doc.scenes ?? []
  const opts = { ...REPORT_RENDER_OPTS, ...renderOpts }
  // Every scene is visited TWICE — once to measure its own scale, once to render
  // — so bytes are cached between the passes rather than read twice.
  //
  // BUT the cache is BUDGETED, because the real folders are not small: measured
  // on disk, Case 4 is 458 MB of meshes with a single 223 MB skull, and Case 2
  // is 279 MB. An unbounded cache holds every mesh of every scene resident at
  // once, on top of the three.js geometry built from them, and a browser tab
  // has a ceiling. Past the budget we simply re-read — slower for the biggest
  // cases, which is the right trade against failing outright.
  const byteCache = new Map()
  let cached = 0
  const partsFor = async (sceneId) => {
    const built = []
    for (const p of sceneParts[sceneId] ?? []) {
      const key = p.meshId ?? p.name
      let bytes = byteCache.get(key)
      if (!bytes) {
        bytes = await loadBytes(p)
        if (cached + bytes.length <= byteBudget) {
          byteCache.set(key, bytes)
          cached += bytes.length
        }
      }
      // `neutral` has to be forwarded EXPLICITLY. This rebuilds the part object
      // rather than spreading it, so anything the draft attaches and this line does
      // not name is silently dropped — which is exactly how splints kept rendering
      // cream after both draft paths had been taught to flag them.
      //
      // `groups`/`neutralGroups` are the per-POLYGROUP declarations and must be
      // forwarded for the same reason: one file can hold a guide AND the anatomy
      // under it, and a per-file flag bleaches both. Pages 20/21 did exactly that.
      built.push({
        bytes, filename: p.name, color: p.color, neutral: p.neutral === true,
        groups: p.groups ?? null, neutralGroups: p.neutralGroups ?? null,
      })
    }
    return built
  }

  const total = scenes.length * 2
  let done = 0
  const tick = (label) => { done += 1; onProgress?.(done, total, label) }

  // ── pass 1: what would each scene frame itself at? ─────────────────────────
  const fitScales = {}
  for (const s of scenes) {
    const parts = await partsFor(s.id)
    const r = await renderReport(new Uint8Array(0),
      { ...opts, ...sceneFrameOpts(s.frame), parts, views: ['A'] })
    fitScales[s.id] = r.fitScale
    tick(`measuring ${s.name}`)
  }

  // ── the shared scale per fit group ─────────────────────────────────────────
  const groupScale = {}
  for (const s of scenes) {
    groupScale[s.fitGroup] = Math.max(groupScale[s.fitGroup] ?? 0, fitScales[s.id] ?? 0)
  }

  // ── pass 2: render every view at the group's scale ─────────────────────────
  // Render exactly the views the document asks of each scene, falling back to
  // `views` only for a scene no page references.
  const needed = viewsForScenes(doc)
  const out = {}
  // An engraved stamp that could not be revealed is INVISIBLE in the output — the page looks
  // right and the lettering is simply absent from the face-on view. PreviewPage warns about
  // that; the Builder had no equivalent, so the identical defect could reach a printed case
  // report in silence. Collected per scene because a report mixes scenes and only some carry
  // a stamp. See reliefStatus() in meshRenderer.js.
  const reliefWarnings = []
  for (const s of scenes) {
    const parts = await partsFor(s.id)
    const want = needed.has(s.id) ? [...needed.get(s.id)] : views
    // Whatever this page asked for that is not a standard view is an extra
    // camera, and has to be named for the renderer to draw it at all.
    const extra = want.filter((v) => !STANDARD_VIEWS.has(v))
    const r = await renderReport(new Uint8Array(0), {
      ...opts, ...sceneFrameOpts(s.frame), parts, views: want,
      fitScaleOverride: groupScale[s.fitGroup],
      // Omitted entirely when there are none, so a call that wants only
      // standard views is byte-identical to before this option existed.
      ...(extra.length ? { extraViews: extra } : {}),
    })
    out[s.id] = r.views
    if (r.relief?.requested && !r.relief.applied) {
      reliefWarnings.push(
        `${s.name}: engraved text may not read in the face-on view (Posterior, or Inferior ` +
        'for a base stamp) — no engraved plate was found and the self-shadow relief pass is ' +
        'off. Check that view before sending this out.',
      )
    }
    // The letter outline gets the same hop, for the same reason. A refusal is invisible in the
    // output — the page looks correct and the lettering simply reads faintly — and the Builder
    // is the path with no preview of an individual view, so a silent one here is worse than in
    // PreviewPage. The relief warning above was plumbed through this exact function after the
    // identical omission; this is not a second lesson, it is the same one.
    if (r.edge?.refused) {
      reliefWarnings.push(
        `${s.name}: the engraved text has no outline in the ${r.edge.refusedView || 'face-on'} ` +
        'view because the characters render too small there to carry one. The text is still ' +
        'engraved; check it reads before sending this out.',
      )
    }
    tick(`rendering ${s.name}`)
  }

  return { views: out, fitScales, groupScale, reliefWarnings }
}

/**
 * Resolve a layout image descriptor to pixels.
 *
 * `buildCaseReport` hands back the very `{kind:'scene', sceneId, view}` the
 * layout emitted; a missing render draws an empty box rather than throwing, so
 * one failed scene cannot cost the whole document.
 */
/**
 * `assets` — images the operator supplied for a page (the colour-map page takes
 * the team's ZBrush render), keyed by asset id. Optional, so every existing
 * caller resolves exactly as before.
 */
export const makeResolveImage = (rendered, assets = {}) => async (source) => {
  if (source?.kind === 'asset') return assets[source.assetId] ?? null
  return source?.kind === 'scene' ? rendered[source.sceneId]?.[source.view] ?? null : null
}
